import { useState } from 'react'
import { X, Instagram, Facebook, Linkedin, Twitter, Youtube, Globe, TrendingUp, Pencil } from 'lucide-react'
import { Badge } from '@/components/common/Badge'
import { StatCard } from '@/components/common/StatCard'
import { MetricsChart } from './MetricsChart'
import { CampaignFormModal } from './CampaignFormModal'
import { cn } from '@/lib/utils'
import type { AdCampaign, CampaignStatus, Platform } from '@/types'

// Extended campaign with optional performance metrics (from ADV page computed data)
type CampaignExt = AdCampaign & {
  impressions?: number
  clicks?: number
  ctr?: number
  cpc?: number
  roas?: number
}

const PLATFORM_ICON: Record<Platform, React.ReactNode> = {
  INSTAGRAM: <Instagram className="w-4 h-4" />,
  FACEBOOK: <Facebook className="w-4 h-4" />,
  LINKEDIN: <Linkedin className="w-4 h-4" />,
  TWITTER: <Twitter className="w-4 h-4" />,
  TIKTOK: <TrendingUp className="w-4 h-4" />,
  YOUTUBE: <Youtube className="w-4 h-4" />,
  PINTEREST: <Globe className="w-4 h-4" />,
}

const PLATFORM_LABEL: Record<Platform, string> = {
  INSTAGRAM: 'Instagram', FACEBOOK: 'Facebook', LINKEDIN: 'LinkedIn',
  TWITTER: 'Twitter / X', TIKTOK: 'TikTok', YOUTUBE: 'YouTube', PINTEREST: 'Pinterest',
}

const STATUS_VARIANT: Record<CampaignStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  ACTIVE: 'success', PAUSED: 'warning', DRAFT: 'neutral',
  PENDING_REVIEW: 'info', COMPLETED: 'info', ARCHIVED: 'neutral',
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  ACTIVE: 'Attiva', PAUSED: 'In pausa', DRAFT: 'Bozza',
  PENDING_REVIEW: 'In revisione', COMPLETED: 'Completata', ARCHIVED: 'Archiviata',
}

type Tab = 'overview' | 'metriche' | 'creativita'

interface CampaignDetailModalProps {
  campaign: CampaignExt
  onClose: () => void
  onEdit?: (campaign: CampaignExt) => void
}

const MOCK_DAILY_SPEND = [
  { label: 'Lun', value: 180 },
  { label: 'Mar', value: 210 },
  { label: 'Mer', value: 195 },
  { label: 'Gio', value: 240 },
  { label: 'Ven', value: 290 },
  { label: 'Sab', value: 160 },
  { label: 'Dom', value: 130 },
]

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between py-2 border-b border-slate-200 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-xs font-medium text-slate-800 text-right max-w-[60%] tabular-nums">{value}</span>
    </div>
  )
}

export function CampaignDetailModal({ campaign, onClose, onEdit }: CampaignDetailModalProps) {
  const [tab, setTab] = useState<Tab>('overview')
  const [showEditForm, setShowEditForm] = useState(false)

  const fmt = (n: number, opts?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat('it-IT', opts).format(n)
  const fmtCurrency = (n: number) => fmt(n, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

  const spentPct = campaign.totalBudget > 0 ? Math.min((campaign.spentBudget / campaign.totalBudget) * 100, 100) : 0
  const fmtImpr = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n)

  const impressions = campaign.impressions ?? 0
  const clicks = campaign.clicks ?? 0
  const ctr = campaign.ctr ?? 0
  const cpc = campaign.cpc ?? 0
  const roas = campaign.roas ?? 0

  function handleEditClick() {
    if (onEdit) {
      onEdit(campaign)
    } else {
      setShowEditForm(true)
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-3xl shadow-modal border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-slate-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-slate-50 rounded-lg text-slate-500">
                {campaign.platforms[0] ? PLATFORM_ICON[campaign.platforms[0]] : null}
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">{campaign.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-500">{campaign.client?.name}</span>
                  <span className="text-slate-300">·</span>
                  <span className="text-xs text-slate-500">{campaign.platforms.map(p => PLATFORM_LABEL[p]).join(', ')}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_VARIANT[campaign.status as CampaignStatus]} dot>
                {STATUS_LABEL[campaign.status as CampaignStatus]}
              </Badge>
              {/* Edit button */}
              <button
                type="button"
                onClick={handleEditClick}
                title="Modifica campagna"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Modifica
              </button>
              <button
                onClick={onClose}
                className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 px-5">
            {(['overview', 'metriche', 'creativita'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium capitalize border-b-2 transition-colors -mb-px',
                  tab === t
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                )}
              >
                {t === 'overview' ? 'Overview' : t === 'metriche' ? 'Metriche' : 'Creativita'}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {tab === 'overview' && (
              <div className="space-y-5">
                {/* Info */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Informazioni</p>
                  <InfoRow label="Obiettivo" value={campaign.objective ?? '—'} />
                  <InfoRow label="Tipo campagna" value={campaign.type === 'ALWAYS_ON' ? 'Always On' : 'Spot'} />
                  <InfoRow
                    label="Data inizio"
                    value={new Date(campaign.startDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })}
                  />
                  <InfoRow
                    label="Data fine"
                    value={campaign.endDate
                      ? new Date(campaign.endDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
                      : 'Continuativa'}
                  />
                  <InfoRow label="Piattaforme" value={campaign.platforms.map(p => PLATFORM_LABEL[p]).join(', ')} />
                </div>

                {/* Budget breakdown */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Budget</p>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-slate-700 tabular-nums">
                      {fmtCurrency(campaign.spentBudget)} speso
                    </span>
                    <span className="text-sm font-semibold text-slate-900 tabular-nums">
                      {fmtCurrency(campaign.totalBudget)} totale
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-1">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        spentPct > 90 ? 'bg-danger-500' : spentPct > 70 ? 'bg-warning-500' : 'bg-primary-500'
                      )}
                      style={{ width: `${spentPct}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 tabular-nums">{spentPct.toFixed(0)}% del budget utilizzato</p>
                </div>

                {/* Key metrics summary */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Impressions', value: fmtImpr(impressions) },
                    { label: 'Click', value: fmtImpr(clicks) },
                    { label: 'CTR', value: `${ctr.toFixed(2)}%` },
                    { label: 'CPC', value: fmtCurrency(cpc) },
                    { label: 'Spesa', value: fmtCurrency(campaign.spentBudget) },
                    { label: 'ROAS', value: roas > 0 ? `${roas.toFixed(1)}x` : '—' },
                  ].map((m) => (
                    <StatCard key={m.label} label={m.label} value={m.value} />
                  ))}
                </div>
              </div>
            )}

            {tab === 'metriche' && (
              <div className="space-y-5">
                {impressions > 0 ? (
                  <MetricsChart
                    data={MOCK_DAILY_SPEND}
                    title="Spesa giornaliera (ultimi 7 giorni)"
                    valueFormatter={(v) => `€${v}`}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <p className="text-sm font-medium text-slate-600 mb-1">Nessuna metrica disponibile</p>
                    <p className="text-xs">Le metriche verranno visualizzate dopo l'attivazione della campagna</p>
                  </div>
                )}
                {impressions > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          {['Data', 'Impressions', 'Click', 'CTR', 'Spesa', 'ROAS'].map((h) => (
                            <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {MOCK_DAILY_SPEND.map((day, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-3 py-2 text-slate-700">{day.label}</td>
                            <td className="px-3 py-2 text-slate-600 tabular-nums">{fmt(day.value * 420)}</td>
                            <td className="px-3 py-2 text-slate-600 tabular-nums">{fmt(day.value * 7)}</td>
                            <td className="px-3 py-2 text-slate-600 tabular-nums">1.67%</td>
                            <td className="px-3 py-2 text-slate-600 tabular-nums">{fmtCurrency(day.value)}</td>
                            <td className="px-3 py-2 font-semibold text-success-600 tabular-nums">3.2x</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === 'creativita' && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Creativita pubblicitarie</p>
                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
                    >
                      <div className="text-center">
                        <div className="w-8 h-8 bg-slate-200 rounded-md mx-auto mb-2" />
                        <p className="text-[9px] text-slate-400">Creativa {i + 1}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inline edit form — opens on top of detail modal */}
      {showEditForm && (
        <CampaignFormModal
          campaign={campaign}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false)
            onClose()
          }}
        />
      )}
    </>
  )
}
